import {BehaviorSubject, Observable, ReplaySubject, Scheduler} from "rxjs";
import {addUiId, AlertDialogRule, defaultRule, stripExcessValues} from "../models/AlertDialogRule";
import {ArrayUtil} from "../../../../js/util/ArrayUtil";
import {NewTradingAlertDefinition, TradingAlertDefinition} from "../../../models/alert/TradingAlert";
import {TradingAlertStatisticsService} from "../../../services/TradingAlertStatisticsService";
import {UnderlyingStatistics} from "../../../models/UnderlyingStatistics";
import {CodeAndExchange} from "../../../../instruments/types/CodeAndExchange";
import {ObjectUtil} from "../../../../js/util/ObjectUtil";

type ModelType = NewTradingAlertDefinition | TradingAlertDefinition | null;

export class AlertDialogStore {

    private readonly statistics$ = new ReplaySubject<UnderlyingStatistics>(1);
    private readonly security$ = new ReplaySubject<CodeAndExchange>(1);
    private readonly description$ = new BehaviorSubject("");
    private readonly rules$ = new BehaviorSubject<ReadonlyArray<AlertDialogRule>>([]);

    private readonly alert$ = new BehaviorSubject<ModelType>(null);

    private readonly destroyed$ = new ReplaySubject<boolean>(1);

    public constructor(private tradingAlertStatisticsService: TradingAlertStatisticsService,
                       private initialState?: TradingAlertDefinition) {
        this.init();
        if (initialState != null) {
            const security = ObjectUtil.pick(initialState, "code", "exchange");
            this.alert$.next(initialState);
            this.security$.next(security);
            this.description$.next(initialState.description);
            const rules = initialState.rules.map(addUiId);
            this.rules$.next(rules);
        }
    }

    private init() {
        this.updateStatistics();
        this.updateAlertState();
    }

    private updateStatistics() {
        this.security$
            .takeUntil(this.destroyed$)
            .switchMap((security) => this.tradingAlertStatisticsService.for(security))
            .subscribe((stats) => this.statistics$.next(stats));
    }

    private updateAlertState() {
        const sources$ = Observable.combineLatest(this.security$, this.description$, this.rules$)
                                   .observeOn(Scheduler.async);
        sources$.withLatestFrom(this.alert$)
                .takeUntil(this.destroyed$)
                .map(([[security, description, rules], alert]) => {
                    const alertRules = rules.map(stripExcessValues);
                    if ((alert == null) || !("id" in alert) || (alert.id == null)) {
                        return {
                            code: security.code,
                            exchange: security.exchange,
                            description,
                            rules: alertRules
                        };
                    } else {
                        return {
                            id: alert.id,
                            code: security.code,
                            exchange: security.exchange,
                            description,
                            rules: alertRules
                        };
                    }
                })
                .subscribe((alert) => this.alert$.next(alert));
    }

    public get security(): Observable<CodeAndExchange> {
        return this.security$.observeOn(Scheduler.async);
    }

    public get statistics() {
        return this.statistics$.observeOn(Scheduler.async);
    }

    public get description(): Observable<string> {
        return this.description$.observeOn(Scheduler.async);
    }

    public get rules(): Observable<ReadonlyArray<AlertDialogRule>> {
        return this.rules$
                   .map((rules) => ArrayUtil.clone(rules))
                   .observeOn(Scheduler.async);
    }

    public get alert(): Observable<ModelType> {
        return this.alert$.observeOn(Scheduler.async);
    }

    public updateSecurity(security: CodeAndExchange) {
        this.security$.next(security);
    }

    public updateDescription(description: string) {
        this.description$.next(description);
    }

    public addRule() {
        const rule = defaultRule();
        const current = this.rules$.getValue();
        const updated = [rule].concat(current);
        this.rules$.next(updated);
    }

    public removeRule(id: string) {
        const current = this.rules$.getValue();
        const updated = current.filter((r) => r.uiid !== id);
        this.rules$.next(updated);
    }

    public replaceRule(rule: AlertDialogRule) {
        const current = this.rules$.getValue();
        const updated = ArrayUtil.replaceBy(current, rule, (r) => r.uiid === rule.uiid);
        this.rules$.next(updated);
    }

    public destroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }
}
